import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { EyeIcon, UserIcon, UserPlusIcon } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import type {
  Incident,
  IncidentSeverity,
  IncidentStatus,
  IncidentType,
} from "@/lib/queries/incidents";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
  INCIDENT_TYPE,
  incidentSeveritySchema,
  incidentStatusSchema,
  incidentTypeSchema,
  incidentsListQueryOptions,
  useIncidentsList,
} from "@/lib/queries/incidents";

import { Button } from "@/components/ui/button";

import { api } from "@/lib/http";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Actions } from "@/components/ui/actions";
import { StatusBadge } from "@/components/ui/status-badge";

const searchSchema = z.object({
  page: z.number().int().positive().catch(1),
  per_page: z.number().int().positive().max(100).catch(10),
  status: incidentStatusSchema.optional(),
  severity: incidentSeveritySchema.optional(),
  type: incidentTypeSchema.optional(),
  order_by: z.string().catch("-created_at"),
});

export const Route = createFileRoute("/_app/incidents/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  loaderDeps: ({ search }) => search,
  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(incidentsListQueryOptions(deps));
  },
});

const columns = [
  {
    header: "Type",
    accessorKey: "type",
    size: 60,
    cell: ({ getValue }) => (
      <Badge variant="outline">{getValue<IncidentType>()}</Badge>
    ),
  },
  {
    header: "Severity",
    accessorKey: "severity",
    size: 60,
    cell: ({ getValue }) => {
      const severity = getValue<IncidentSeverity>();
      const variant =
        severity === "Critical"
          ? "error"
          : severity === "High"
            ? "warning"
            : severity === "Medium"
              ? "warning"
              : "success";
      return <StatusBadge variant={variant}>{severity}</StatusBadge>;
    },
  },
  {
    header: "Status",
    accessorKey: "status",
    size: 60,
    cell: ({ getValue }) => {
      const status = getValue<IncidentStatus>();
      const variant =
        status === "OPEN"
          ? "default"
          : status === "ASSIGNED"
            ? "info"
            : status === "IN_REVIEW"
              ? "warning"
              : status === "RESOLVED"
                ? "success"
                : "error";
      return <StatusBadge variant={variant}>{status}</StatusBadge>;
    },
  },
  {
    header: "User",
    accessorKey: "triggered_by_user.full_name",
    size: 60,
    enableSorting: false,
    cell: ({ getValue }) => {
      const user = getValue<string | undefined>();
      return user ? (
        <div className="flex items-center gap-2">
          <UserIcon className="h-4 w-4" />
          <span>{user}</span>
        </div>
      ) : (
        <span className="text-muted-foreground">N/A</span>
      );
    },
  },
  {
    header: "Created",
    accessorKey: "created_at",
    size: 60,
    cell: ({ getValue }) => {
      return new Date(getValue<string>()).toLocaleDateString();
    },
  },
  {
    header: "Updated",
    accessorKey: "updated_at",
    size: 60,
    cell: ({ getValue }) => {
      const value = getValue<null | string>();
      return value === null ? "N/A" : new Date(value).toLocaleDateString();
    },
  },
  {
    header: "Actions",
    id: "actions",
    size: 60,
    enableSorting: false,
    meta: { align: "right" },
    cell: ({ row: { original } }) => (
      <Actions>
        <ViewDialog asChild incident={original}>
          <Button
            size="lg"
            variant="ghost"
            className="w-full justify-start text-base"
          >
            <EyeIcon className="size-5" />
            View
          </Button>
        </ViewDialog>
        <AssignDialog asChild incident={original} />
      </Actions>
    ),
  },
] as const satisfies Array<ColumnDef<Incident>>;

function RouteComponent() {
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();

  console.log("Search params:", searchParams);

  const incidents = useIncidentsList(searchParams);

  const isDescending = searchParams.order_by.startsWith("-");
  const sorting = [
    {
      desc: isDescending,
      id: isDescending ? searchParams.order_by.slice(1) : searchParams.order_by,
    },
  ];

  const table = useReactTable({
    data: incidents.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
      sorting,
    },
    onSortingChange(s) {
      const newSorting = typeof s === "function" ? s(sorting) : s;
      const lastSort = newSorting.at(-1);

      navigate({
        from: Route.fullPath,
        search: (old) => ({
          ...old,
          page: 1,
          order_by: lastSort
            ? `${lastSort.desc ? "-" : ""}${lastSort.id}`
            : "-created_at",
        }),
      });
    },
    rowCount: incidents.data?.count ?? 1,
    manualPagination: true,
    manualFiltering: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <h1 className="text-3xl font-bold">Incidents</h1>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
          <Select
            value={searchParams.status || "ALL"}
            onValueChange={(value: IncidentStatus | "ALL") =>
              navigate({
                search: (old) => ({
                  ...old,
                  status: value === "ALL" ? undefined : value,
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[130px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              {INCIDENT_STATUS.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={searchParams.severity || "ALL"}
            onValueChange={(value: IncidentSeverity | "ALL") =>
              navigate({
                search: (old) => ({
                  ...old,
                  severity: value === "ALL" ? undefined : value,
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[140px]">
              <SelectValue placeholder="All Severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Severities</SelectItem>
              {INCIDENT_SEVERITY.map((sev) => (
                <SelectItem key={sev} value={sev}>
                  {sev}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={searchParams.type || "ALL"}
            onValueChange={(value: IncidentType | "ALL") =>
              navigate({
                search: (old) => ({
                  ...old,
                  type: value === "ALL" ? undefined : value,
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[120px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Types</SelectItem>
              {INCIDENT_TYPE.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            className="border-primary text-primary hover:bg-primary/5 hover:text-primary"
            onClick={() =>
              navigate({
                search: (old) => ({
                  ...old,
                  status: undefined,
                  severity: undefined,
                  type: undefined,
                  page: 1,
                }),
              })
            }
          >
            Reset Filters
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <GenericTable sorting table={table} isLoading={incidents.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

// TODO: test the dialog components once there's actual data to test with
function ViewDialog({
  children,
  incident,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean; incident: Incident }>) {
  return (
    <Dialog>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Incident Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Info label="Type" value={incident.type} />
            <div>
              <label className="text-sm font-medium">Severity</label>
              <div className="mt-1">
                <StatusBadge
                  variant={
                    incident.severity === "Critical"
                      ? "error"
                      : incident.severity === "High"
                        ? "warning"
                        : incident.severity === "Medium"
                          ? "warning"
                          : "success"
                  }
                >
                  {incident.severity}
                </StatusBadge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <div className="mt-1">
                <StatusBadge
                  variant={
                    incident.status === "OPEN"
                      ? "default"
                      : incident.status === "ASSIGNED"
                        ? "info"
                        : incident.status === "IN_REVIEW"
                          ? "warning"
                          : incident.status === "RESOLVED"
                            ? "success"
                            : "error"
                  }
                >
                  {incident.status}
                </StatusBadge>
              </div>
            </div>
            <Info
              label="Created"
              value={new Date(incident.created_at).toLocaleString()}
            />
          </div>

          {incident.triggered_by_user && (
            <Info
              label="Triggered By"
              value={`${incident.triggered_by_user.full_name} (${incident.triggered_by_user.email})`}
            />
          )}

          {incident.message_entity && (
            <div>
              <label className="text-sm font-medium">Detected PII</label>
              <div className="mt-1 rounded bg-muted p-3">
                <p className="font-mono text-sm">
                  {incident.message_entity.entity_text}
                </p>
                {incident.message_entity.masked_entity_text && (
                  <p className="mt-1 font-mono text-sm text-muted-foreground">
                    Masked: {incident.message_entity.masked_entity_text}
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  Confidence: {incident.message_entity.confidence_score} |{" "}
                  Critical:{" "}
                  {incident.message_entity.is_critical_pii ? "Yes" : "No"}
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <p className="text-sm text-muted-foreground">{value ?? "—"}</p>
    </div>
  );
}

function AssignDialog({
  incident,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean; incident: Incident }>) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: { assigned_to_user_id: string }) =>
      api.raw.patch(`api/v1/incidents/${incident.id}/assign`, { json: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["incidents", "list"] });
      setOpen(false);
      toast.success("Incident assigned successfully");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>
        <Button
          size="lg"
          variant="ghost"
          className="w-full justify-start text-base"
        >
          <UserPlusIcon className="size-5" />
          Assign
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Incident</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Assign this incident to a team member for investigation.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={mutation.isPending}
              onClick={() =>
                mutation.mutate({ assigned_to_user_id: "user-id-placeholder" })
              }
            >
              Assign
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
